import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { productsService, ordersService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { Product } from '../../types/product.type';
import { sitesService } from '../../services/sites.service';
import { ProductCategory } from '../../constants';

interface LightsailBlueprint {
  id: string;
  name: string;
  description: string;
  version: string;
  platform: string;
  group: string;
}

interface LightsailBundle {
  id: string;
  name: string;
  price: number;
  cpuCount: number;
  ramSizeInGb: number;
  diskSizeInGb: number;
  transferPerMonthInGb: number;
}

interface SiteConfig {
  name: string;
  description: string;
  blueprintId: string;
  bundleId: string;
  customDomain?: string;
  environment: 'production' | 'staging' | 'development';
  region: string;
}

const CreateSite: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState<SiteConfig>({
    name: '',
    description: '',
    blueprintId: '',
    bundleId: 'micro_2_0',
    customDomain: '',
    environment: 'production',
    region: 'us-east-1',
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [blueprints, setBlueprints] = useState<LightsailBlueprint[]>([]);
  const [bundles, setBundles] = useState<LightsailBundle[]>([]);
  const [loadingBlueprints, setLoadingBlueprints] = useState(true);
  const [loadingBundles, setLoadingBundles] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  const hasFetchedData = useRef(false);
  const { accessToken, isLoading: authLoading } = useAuth();

  const regions = [
    { id: 'us-east-1', name: 'US East (N. Virginia)' },
    { id: 'us-west-2', name: 'US West (Oregon)' },
    { id: 'eu-west-1', name: 'EU (Ireland)' },
    { id: 'ap-southeast-1', name: 'Asia Pacific (Singapore)' },
  ];

  useEffect(() => {
    if (authLoading || hasFetchedData.current) return;
    hasFetchedData.current = true;

    const fetchData = async () => {
      if (!accessToken) {
        const { waitForToken } = await import('../../utils/tokenHelper');
        await waitForToken(3000, 200);
      }

      // Fetch blueprints
      try {
        setLoadingBlueprints(true);
        const bpResponse = await sitesService.getBlueprints();
        setBlueprints(bpResponse.blueprints || []);
      } catch (error) {
        console.error('Error fetching blueprints:', error);
        setBlueprints([]);
      } finally {
        setLoadingBlueprints(false);
      }

      // Fetch bundles
      try {
        setLoadingBundles(true);
        const buResponse = await sitesService.getBundles();
        setBundles(buResponse.bundles || []);
      } catch (error) {
        console.error('Error fetching bundles:', error);
        setBundles([]);
      } finally {
        setLoadingBundles(false);
      }

      // Fetch SITE product (for orderId)
      try {
        const response = await productsService.getProductsByCategory(ProductCategory.SITE);
        const productsArray = Array.isArray(response?.products) ? response.products : [];
        setProducts(productsArray);
      } catch (error) {
        console.error('Error fetching SITE products:', error);
        setProducts([]);
        hasFetchedData.current = false;
      }
    };

    fetchData();
  }, [accessToken, authLoading]);

  const handleNext = () => {
    if (step < 3 && isStepValid()) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return config.name && config.blueprintId;
      case 2:
        return !!config.bundleId;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleCreate = async () => {
    if (!config.name || !config.blueprintId || !config.bundleId) {
      console.error('Missing required configuration');
      return;
    }

    setIsCreating(true);
    try {
      const productId = products[0]?.id; // "lightsail-site"

      if (!productId) {
        console.error('No SITE product found');
        setIsCreating(false);
        return;
      }

      const orderData = {
        productId: productId,
        quantity: 1,
        metadata: {
          type: 'SITE',
          siteConfig: {
            name: config.name,
            description: config.description,
            blueprintId: config.blueprintId,
            bundleId: config.bundleId,
            customDomain: config.customDomain,
            region: config.region,
            environment: config.environment,
            availabilityZone: `${config.region}a`,
          },
        },
      };

      console.log('Creating order:', orderData);
      const createdOrder = await ordersService.create(orderData);
      console.log('Order created:', createdOrder);

      navigate(`/payment-selection/${createdOrder.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error creating order. Please try again.');
      setIsCreating(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Site Name *
              </label>
              <input
                type="text"
                value={config.name}
                onChange={(e) => setConfig({ ...config, name: e.target.value })}
                placeholder="my-awesome-site"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={config.description}
                onChange={(e) => setConfig({ ...config, description: e.target.value })}
                placeholder="Brief description of your site..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Blueprint *
              </label>
              {loadingBlueprints ? (
                <div className="text-gray-500">Loading available blueprints...</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {blueprints.map((bp) => (
                    <button
                      key={bp.id}
                      onClick={() => setConfig({ ...config, blueprintId: bp.id })}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        config.blueprintId === bp.id
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="font-medium text-gray-900 dark:text-white text-sm">{bp.name}</div>
                      <div className="text-xs text-gray-500 mt-1">v{bp.version}</div>
                    </button>
                  ))}
                </div>
              )}
              {config.blueprintId && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {blueprints.find((b) => b.id === config.blueprintId)?.name}:
                    </span>{' '}
                    {blueprints.find((b) => b.id === config.blueprintId)?.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Plan *
              </label>
              {loadingBundles ? (
                <div className="text-gray-500">Loading available plans...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {bundles.map((bundle) => (
                    <button
                      key={bundle.id}
                      onClick={() => setConfig({ ...config, bundleId: bundle.id })}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        config.bundleId === bundle.id
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="font-medium text-gray-900 dark:text-white">{bundle.name}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        {bundle.cpuCount} vCPU · {bundle.ramSizeInGb} GB RAM · {bundle.diskSizeInGb} GB SSD
                      </div>
                      <div className="text-sm text-gray-500">
                        {bundle.transferPerMonthInGb} GB Transfer
                      </div>
                      <div className="text-lg font-bold text-blue-600 mt-2">
                        ${bundle.price}/mo
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Domain (Optional)
              </label>
              <input
                type="text"
                value={config.customDomain}
                onChange={(e) => setConfig({ ...config, customDomain: e.target.value })}
                placeholder="example.com"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Region
              </label>
              <select
                value={config.region}
                onChange={(e) => setConfig({ ...config, region: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-800 dark:text-white"
              >
                {regions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 3:
        const selectedBlueprint = blueprints.find((b) => b.id === config.blueprintId);
        const selectedBundle = bundles.find((b) => b.id === config.bundleId);
        return (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-500">Site Name</span>
                <p className="text-base text-gray-900 dark:text-white">{config.name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Description</span>
                <p className="text-base text-gray-900 dark:text-white">
                  {config.description || 'No description'}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Blueprint</span>
                <p className="text-base text-gray-900 dark:text-white">
                  {selectedBlueprint?.name || config.blueprintId} (v{selectedBlueprint?.version})
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Plan</span>
                <p className="text-base text-gray-900 dark:text-white">
                  {selectedBundle?.name} — {selectedBundle?.cpuCount} vCPU, {selectedBundle?.ramSizeInGb}{' '}
                  GB RAM, {selectedBundle?.diskSizeInGb} GB SSD
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Monthly Price</span>
                <p className="text-base text-blue-600 font-bold">${selectedBundle?.price}/mo</p>
              </div>
              {config.customDomain && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Custom Domain</span>
                  <p className="text-base text-gray-900 dark:text-white">{config.customDomain}</p>
                </div>
              )}
              <div>
                <span className="text-sm font-medium text-gray-500">Region</span>
                <p className="text-base text-gray-900 dark:text-white">
                  {regions.find((r) => r.id === config.region)?.name || config.region}
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 py-6">
      <button
        onClick={() => navigate('/sites')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
      >
        <ArrowLeftOutlined />
        <span className="text-sm font-medium">Back to Sites</span>
      </button>

      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Create New Site</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Deploy your website with just a few clicks. Choose your platform and we'll handle the rest.
      </p>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[
            { num: 1, label: 'Basic Info' },
            { num: 2, label: 'Plan & Config' },
            { num: 3, label: 'Review' },
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                    step >= s.num
                      ? 'bg-black text-white dark:bg-white dark:text-black'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                  }`}
                >
                  {step > s.num ? <CheckCircleOutlined /> : s.num}
                </div>
                <span
                  className={`text-xs mt-2 font-medium ${
                    step >= s.num ? 'text-gray-900 dark:text-white' : 'text-gray-500'
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {idx < 2 && (
                <div
                  className={`h-1 flex-1 mx-2 rounded transition-all ${
                    step > s.num ? 'bg-black dark:bg-white' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {renderStep()}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className={`px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors ${
            step === 1
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          Back
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/sites')}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                isStepValid()
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating order...' : 'Create Order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateSite;